/**
 * Follow-up Scheduler Modal
 * 
 * Modal for scheduling follow-up appointments based on AI recommendations.
 * Pre-fills timing from the parsed AI recommendation.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calendar,
  Sparkles,
  CheckCircle2,
  Loader2,
  Clock,
  CalendarDays,
} from 'lucide-react';
import { ClinicalAction } from '@/lib/intellihealthActions';
import { FHIRPatient } from '@/services/fhirPatientsService';
import { toast } from '@/hooks/use-toast';

interface FollowupSchedulerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: ClinicalAction | null;
  patient: FHIRPatient | null;
  onSubmit?: (scheduleData: FollowupScheduleData) => Promise<void>;
}

export interface FollowupScheduleData {
  patientId: string;
  followupDays: number;
  appointmentType: string;
  notes: string;
  sendReminder: boolean;
}

const FOLLOWUP_OPTIONS = [
  { value: 7, label: '1 Week' },
  { value: 14, label: '2 Weeks' },
  { value: 21, label: '3 Weeks' },
  { value: 30, label: '1 Month' },
  { value: 45, label: '6 Weeks' },
  { value: 60, label: '2 Months' },
  { value: 90, label: '3 Months' },
  { value: 180, label: '6 Months' },
  { value: 365, label: '1 Year' },
];

const APPOINTMENT_TYPES = [
  { value: 'follow_up', label: 'Follow-up Visit' },
  { value: 'lab_review', label: 'Lab Review' },
  { value: 'medication_check', label: 'Medication Check' },
  { value: 'wellness', label: 'Wellness Check' },
  { value: 'consultation', label: 'Consultation' },
];

const FollowupSchedulerModal: React.FC<FollowupSchedulerModalProps> = ({
  open,
  onOpenChange,
  action,
  patient,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [followupDays, setFollowupDays] = useState<number>(
    action?.metadata.followupDays || 30
  );
  const [appointmentType, setAppointmentType] = useState('follow_up');
  const [notes, setNotes] = useState('');
  const [sendReminder, setSendReminder] = useState(true);

  // Reset form when action changes
  React.useEffect(() => {
    if (action) {
      setFollowupDays(action.metadata.followupDays || 30);
      setNotes('');
      setSendReminder(true);
    }
  }, [action]);

  const scheduledDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + followupDays);
    return date;
  }, [followupDays]);

  const handleSubmit = async () => {
    if (!patient) {
      toast({
        title: 'Error',
        description: 'No patient selected',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const scheduleData: FollowupScheduleData = {
        patientId: patient.id,
        followupDays,
        appointmentType,
        notes,
        sendReminder,
      };

      if (onSubmit) {
        await onSubmit(scheduleData);
      }

      toast({
        title: 'Success',
        description: `Follow-up scheduled for ${scheduledDate.toLocaleDateString()}`,
      });

      onOpenChange(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to schedule follow-up'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!action) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[hsl(var(--ih-bg))] border-[hsl(var(--ih-border))]">
        <DialogHeader>
          <DialogTitle className="text-[hsl(var(--ih-text))] flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="h-4 w-4 text-amber-400" />
            </div>
            Schedule Follow-up
          </DialogTitle>
          <DialogDescription className="text-[hsl(var(--ih-text-muted))]">
            {patient && (
              <span>
                For <span className="text-[hsl(var(--ih-text))] font-medium">{patient.first_name} {patient.last_name}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* AI Source Indicator */}
        {action.metadata.followupType && (
          <div className="flex items-center gap-2 px-3 py-2 bg-brand-teal/10 border border-brand-teal/20 rounded-lg mb-4">
            <Sparkles className="h-4 w-4 text-brand-teal" />
            <span className="text-xs text-brand-teal/70">
              AI recommends: {action.metadata.followupType}
            </span>
          </div>
        )}

        {/* Follow-up Options */}
        <div className="space-y-4">
          {/* Timing */}
          <div>
            <Label className="text-[hsl(var(--ih-text-secondary))]">Follow-up Timing</Label>
            <Select
              value={followupDays.toString()}
              onValueChange={(v) => setFollowupDays(parseInt(v))}
            >
              <SelectTrigger className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))] mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))]">
                {FOLLOWUP_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value.toString()} className="text-[hsl(var(--ih-text))]">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Appointment Type */}
          <div>
            <Label className="text-[hsl(var(--ih-text-secondary))]">Appointment Type</Label>
            <Select
              value={appointmentType}
              onValueChange={setAppointmentType}
            >
              <SelectTrigger className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))] mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))]">
                {APPOINTMENT_TYPES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-[hsl(var(--ih-text))]">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scheduled Date Preview */}
          <div className="bg-[hsl(var(--ih-bg-elevated))] rounded-lg p-4 border border-[hsl(var(--ih-border))]">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <CalendarDays className="h-6 w-6 text-amber-400" />
              </div>
              <div>
                <p className="text-[hsl(var(--ih-text))] font-medium">
                  {scheduledDate.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </p>
                <p className="text-[hsl(var(--ih-text-muted))] text-sm">
                  {followupDays} days from today
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-[hsl(var(--ih-text-secondary))]">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for follow-up..."
              className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))] mt-1"
              rows={2}
            />
          </div>

          {/* Reminder Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendReminder"
              checked={sendReminder}
              onChange={(e) => setSendReminder(e.target.checked)}
              className="rounded border-[hsl(var(--ih-border))] bg-[hsl(var(--ih-bg-elevated))] text-amber-500 focus:ring-amber-500"
            />
            <Label htmlFor="sendReminder" className="text-[hsl(var(--ih-text-secondary))] text-sm">
              Send reminder to patient
            </Label>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text-secondary))]"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scheduling...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Schedule Follow-up
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FollowupSchedulerModal;
